import { useMemo, useRef } from 'react'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import useStorefrontUrl from '@/app/buy/seller/[sellerSlug]/useStorefrontUrl'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useViewItemListOnVisible } from '@/helpers/telemetry/useViewItemListOnVisible'

import ListingCard from '../listingCard/ListingCard'
import ListingCardSkeleton from '../listingCard/ListingCardSkeleton'
import { Grid } from '../ListingGrid/ListingGrid'
import ListingSearchContext from '../search/ListingSearchProvider/ListingSearchContext'
import { MoreFromSellerCard } from './MoreFromSellerCard'
import { MoreFromSellerSection } from './MoreFromSellerSection'
import { useLimitedListingSearchContext } from './useLimitedListingSearchContext'

export type MoreFromSellerVariant = 'section' | 'card'

export function MoreFromSellerContent({
  currentListing,
  excludeListingId,
  storefront,
  variant = 'section',
}: {
  currentListing?: {
    lot: { _id: string }
    accountId: string
    seller: {
      storefront: { name?: string } | null
      account: { displayName: string | null }
    }
  }
  excludeListingId: string
  storefront: StoreFrontDto
  variant?: MoreFromSellerVariant
}) {
  const { trackButtonClicked } = useAnalytics()
  const storefrontUrl = useStorefrontUrl(storefront)
  const contextValue = useLimitedListingSearchContext({
    storefront,
    excludeListingId,
  })
  const filteredListings = contextValue.data?.listings ?? []
  const containerRef = useRef<HTMLDivElement>(null)

  const itemListId = 'carousel-more-from-seller-pdp'
  const itemListName = storefront.name || 'More from this Seller'

  // Track GTM view_item_list when carousel becomes visible in viewport
  useViewItemListOnVisible({
    containerRef,
    listings: filteredListings,
    itemListId,
    itemListName,
    enabled: !contextValue.isLoading && filteredListings.length > 0,
  })

  const analyticsData = useMemo(
    () => ({
      ...(contextValue.currentImpressionId && {
        listing_card_impression_id: contextValue.currentImpressionId,
      }),
    }),
    [contextValue.currentImpressionId]
  )

  const handleBrowseAllClick = () => {
    if (currentListing) {
      trackButtonClicked({
        screenName: 'listing_detail_page',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        listing_id: currentListing.lot._id,
        seller_account_id: currentListing.accountId,
        storefront_name: currentListing.seller.storefront?.name,
        seller_display_name: currentListing.seller.account.displayName || '',
        buttonName: 'listing_detail_page_carousel_browse_all',
      })
    }
  }

  if (!contextValue.isLoading && filteredListings.length === 0) {
    return null
  }

  const Shell = variant === 'card' ? MoreFromSellerCard : MoreFromSellerSection

  return (
    <Shell
      containerRef={containerRef}
      isLoading={contextValue.isLoading}
      listingCount={filteredListings.length}
      onBrowseAllClick={handleBrowseAllClick}
      storefront={storefront}
      storefrontUrl={storefrontUrl}
      total={contextValue.data?.total ?? 0}
    >
      <NestableAnalyticsProvider value={analyticsData}>
        <ItemListProvider itemListId={itemListId} itemListName={itemListName}>
          <ListingSearchContext.Provider value={contextValue}>
            <Grid data-more-from-seller-grid>
              {contextValue.isLoading && filteredListings.length === 0
                ? Array.from({ length: 4 }, (_, index) => (
                    <ListingCardSkeleton key={index} />
                  ))
                : filteredListings.map((listing, index) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      screenName={ScreenName.ListingDetails}
                      position={index + 1}
                    />
                  ))}
            </Grid>
          </ListingSearchContext.Provider>
        </ItemListProvider>
      </NestableAnalyticsProvider>
    </Shell>
  )
}
